import * as React from "react";
import {generatePath, Redirect} from "react-router-dom";

import {isRelativeUrl, useLocation} from "@khan/urls";

import PageDisplay from "#data-access/page-display.tsx";

import FullPageRedirect from "./full-page-redirect.dev.tsx";

type NonRouteTableProps = {
    /**
     * The target URL for the redirect.
     */
    readonly to: string;
    /**
     * When `true`, a full-page redirect is performed. Otherwise, it is based
     * on the value of `to`, where relative URLs use React Router routing and
     * absolute URLs are full page loads.
     */
    readonly forceFullPageLoad?: boolean;
    /**
     * When `true`, any search params from the current URL are included in the
     * redirect.
     */
    readonly preserveSearchParams?: boolean;
};

type RouteTableOnlyProps = {
    /**
     * Private, from <Switch>
     */
    computedMatch?: {
        path: string;
        params: Record<string, string | boolean | number>;
    };
    /**
     * The path that the redirect redirects from.
     *
     * This is for use within a route Switch inside React Router.
     */
    readonly from: string;
    /**
     * When `true`, a `from` path that has a trailing slash will only match a
     * `location.pathname` with a trailing slash.
     * This has no effect when there are additional URL segments in the
     * `location.pathname` or `from` is not specified.
     *
     * Ignored if `from` is not specified.
     */
    readonly strict?: boolean;
    /**
     * When `true`, `from` will only match if the path matches the
     * `location.pathname` exactly.
     *
     * Ignored if `from` is not specified.
     */
    readonly exact?: boolean;
    /**
     * When `true`, `from` is matched in a case-sensitive manner.
     *
     * Ignored if `from` is not specified.
     */
    readonly sensitive?: boolean;
};

type RouteTableProps = RouteTableOnlyProps & NonRouteTableProps;

type OptionalAndNull<T> = {[P in keyof T]?: null};

type Props =
    // When used in a route table, we use all the different props as they are.
    | RouteTableProps
    // When not in a route table, we don't support the route table props,
    // so we make them optional (or allowed as null).
    | (NonRouteTableProps & OptionalAndNull<RouteTableOnlyProps>);

/**
 * Render a full page redirect.
 *
 * This is a full redirect in that the entire page is reloaded.
 */
const SmartRedirect = (props: Props): React.ReactElement => {
    const location = useLocation();
    const {to, forceFullPageLoad, preserveSearchParams, ...routeTableProps} =
        props;
    /**
     * We need to calculate the `to` location before we do our
     * smart check for absolute, just to make sure we're passing a valid
     * URL here and since the `computedMatch` may not be available for our
     * Redirect components to use.
     *
     * Frustratingly, the version of path-to-regexp that React Router is
     * using encodes subsitutions. This can leave us with paths like:
     *
     *     `/computing/computer-programming/the%2Fold%2Fpath`
     *
     * So, we have to sub those `%2Fs` back to `/`s.
     */
    const pathname =
        routeTableProps.computedMatch == null
            ? to
            : generatePath(to, routeTableProps.computedMatch.params).replace(
                  /%2F/g,
                  "/",
              );

    const realTo = preserveSearchParams
        ? `${pathname}${location.search}`
        : pathname;

    if (forceFullPageLoad || !isRelativeUrl(realTo)) {
        /**
         * Since the `Switch` component is what uses the `from`, `strict`,
         * `exact`, and `sensitive` props, we don't need to pass them on to
         * the inner components.
         */
        return <FullPageRedirect to={realTo} />;
    }

    /**
     * Since the `Switch` component is what uses the `from`, `strict`,
     * `exact`, and `sensitive` props, we don't need to pass them on to
     * the inner components.
     */
    return (
        <PageDisplay settings={{}}>
            <Redirect to={realTo} />
        </PageDisplay>
    );
};

export default SmartRedirect;
